import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  MonitorIcon,
  PowerIcon,
  RotateCcwIcon,
  SquareIcon,
  PlayIcon,
  CopyIcon,
  WifiIcon,
  TerminalIcon,
  KeyboardIcon,
  AlertCircleIcon,
  CheckCircleIcon,
  LoaderIcon,
  ClipboardIcon,
} from 'lucide-react'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import {
  consoleApi,
  type ConsoleInfo,
  type ConsoleTicket,
} from '@nebula/shared/services/consoleApi'

import SerialConsole from '../components/SerialConsole'
import VNCConsole from '../components/VNCConsole'

/**
 * O ritmo do poll do `console/info` (#754).
 *
 * Cada tick custa round-trips ao Proxmox, e com a VM rodando não há o que descobrir —
 * uma VM não para sozinha. O intervalo curto fica então reservado a quando há transição
 * à vista (VM fora de `running`, ou ação de power recém-pedida), e os eventos que
 * sugerem mudança perguntam na hora, em vez de o relógio esperar por eles.
 *
 * A VM PARADA fica no curto mesmo sem ação recente, e isso é assimetria de propósito:
 * com ela parada, a tela é um botão "Iniciar VM" e quem está ali está esperando o boot —
 * inclusive o pedido de outra aba ou do portal do reseller, que este popup não vê. É
 * também o tick mais barato que existe (sem `network-get-interfaces`, porque não há
 * guest agent a consultar).
 */
const POLL_EM_TRANSICAO_MS = 5000
const POLL_VM_ESTAVEL_MS = 20_000

/**
 * Depois de um start/stop, o `vm_status` do PVE demora a acompanhar o pedido — olhar só
 * o status atual manteria o intervalo longo exatamente durante a transição que o cliente
 * está esperando ver.
 */
const JANELA_APOS_ACAO_MS = 60_000

/**
 * Piso entre dois refetches disparados por evento. O `SerialConsole` reconecta sozinho em
 * backoff (2s, 5s, 10s) e chama `onDisconnect` a cada tentativa: sem o piso, uma VM que
 * parou trocaria o poll de 5s por uma rajada — o mesmo desperdício com outro gatilho.
 *
 * O piso é DOS GATILHOS, não da query: o tick periódico não o consome nem é barrado por
 * ele. No pior caso — console instável reconectando em loop — os dois se somam e o total
 * empata com o poll fixo antigo; o que muda é o preço de cada request, que deixou de
 * abrir sessão de console no node.
 */
const PISO_ENTRE_REFETCHES_MS = 5000

const VMConsolePopupPage = () => {
  const { vmId } = useParams()
  const [searchParams] = useSearchParams()
  const initialProtocol = searchParams.get('protocol') === 'serial' ? 'serial' : 'vnc'
  const [consoleType, setConsoleType] = useState<'vnc' | 'serial'>(
    initialProtocol as 'vnc' | 'serial',
  )
  const [isConnected, setIsConnected] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState('disconnected')
  const [consoleTicket, setConsoleTicket] = useState<ConsoleTicket | null>(null)
  const [_consoleError, setConsoleError] = useState<string | null>(null)
  const [clipboardText, setClipboardText] = useState('')
  const [showClipboard, setShowClipboard] = useState(false)
  const [powerActionLoading, setPowerActionLoading] = useState(false)
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean
    action: string
    message: string
  } | null>(null)
  const [alertModal, setAlertModal] = useState<{
    show: boolean
    type: 'success' | 'error'
    message: string
  } | null>(null)
  const [vncMetrics, setVncMetrics] = useState<{
    resolution: string
    fps: number
    bandwidth: number
  } | null>(null)
  const [isMobile, setIsMobile] = useState(() =>
    typeof window === 'undefined' ? false : window.innerWidth < 768,
  )

  const vncConsoleRef = useRef<any>(null)
  const queryClient = useQueryClient()
  const [pollCurtoAte, setPollCurtoAte] = useState(0)
  const ultimoRefetchRef = useRef(0)

  /** Pergunta o status fora do intervalo, sem deixar o gatilho virar rajada. */
  const perguntarStatusAgora = useCallback(
    (opcoes?: { ignorarPiso?: boolean }) => {
      // A ação de power não consome o piso: ela é evento único (não rajada), e o
      // `onDisconnect` que chega logo depois — o WebSocket caindo quando a VM de fato
      // parou — é o sinal mais rápido de que a transição pedida terminou. Carimbar aqui
      // engoliria justamente ele.
      if (opcoes?.ignorarPiso) {
        queryClient.invalidateQueries({ queryKey: ['consoleInfo', vmId] })
        return
      }
      const agora = Date.now()
      if (agora - ultimoRefetchRef.current < PISO_ENTRE_REFETCHES_MS) {
        return
      }
      ultimoRefetchRef.current = agora
      queryClient.invalidateQueries({ queryKey: ['consoleInfo', vmId] })
    },
    [queryClient, vmId],
  )

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)')
    const syncMobileState = () => setIsMobile(mediaQuery.matches)
    syncMobileState()
    mediaQuery.addEventListener('change', syncMobileState)
    return () => mediaQuery.removeEventListener('change', syncMobileState)
  }, [])

  // Fetch console info
  const { data: consoleInfo, isLoading: isLoadingInfo } = useQuery<ConsoleInfo>({
    queryKey: ['consoleInfo', vmId],
    queryFn: () => consoleApi.getConsoleInfo(vmId!),
    enabled: !!vmId,
    refetchInterval: (query) =>
      query.state.data?.vm_status === 'running' && Date.now() >= pollCurtoAte
        ? POLL_VM_ESTAVEL_MS
        : POLL_EM_TRANSICAO_MS,
  })

  // Auto-select serial when default protocol is serial (if not already set by URL param)
  useEffect(() => {
    if (consoleInfo?.default_protocol === 'serial' && initialProtocol === 'vnc') {
      setConsoleType('serial')
    }
  }, [consoleInfo?.default_protocol]) // eslint-disable-line react-hooks/exhaustive-deps

  // O serial pode deixar de ser anunciado entre um poll e outro (#1078): volta ao VNC em
  // vez de ficar num console que o `console/info` acabou de negar, sem o alternador.
  useEffect(() => {
    if (consoleType === 'serial' && consoleInfo && !consoleInfo.serial_available) {
      setConsoleType('vnc')
    }
  }, [consoleType, consoleInfo])

  // Fetch console ticket when VM is running
  const { data: ticketData } = useQuery<ConsoleTicket>({
    queryKey: ['consoleTicket', vmId, consoleType],
    queryFn: () => consoleApi.getConsoleTicket(vmId!, consoleType),
    enabled: !!vmId && consoleInfo?.console_available === true,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (ticketData) {
      setConsoleTicket(ticketData)
    }
  }, [ticketData])

  const handleKeyboardShortcut = (keys: string) => {
    if (vncConsoleRef.current?.sendKeys) {
      // Convert to lowercase for consistency with VNCConsole implementation
      vncConsoleRef.current.sendKeys(keys.toLowerCase())
    }
  }

  const handleVMAction = async (action: 'start' | 'stop' | 'restart' | 'reset') => {
    const messages = {
      start: 'Tem certeza que deseja iniciar esta VM?',
      stop: 'Tem certeza que deseja parar esta VM?',
      restart: 'Tem certeza que deseja reiniciar esta VM?',
      reset: 'Tem certeza que deseja forçar reinicialização desta VM?',
    }

    setConfirmModal({
      show: true,
      action,
      message: messages[action],
    })
  }

  const executeVMAction = async (action: string) => {
    try {
      setPowerActionLoading(true)
      setConfirmModal(null)

      await consoleApi.controlVM(vmId!, action as any)

      // O cliente acabou de pedir a transição: pergunta o status já e mantém o poll
      // curto até ela assentar. Antes, a tela só reagia no tick seguinte.
      setPollCurtoAte(Date.now() + JANELA_APOS_ACAO_MS)
      perguntarStatusAgora({ ignorarPiso: true })

      setAlertModal({
        show: true,
        type: 'success',
        message: `VM ${action === 'start' ? 'iniciada' : action === 'stop' ? 'parada' : 'reiniciada'} com sucesso`,
      })

      setTimeout(() => setAlertModal(null), 3000)
    } catch (error: any) {
      setAlertModal({
        show: true,
        type: 'error',
        message:
          error.response?.data?.detail ||
          `Erro ao ${action === 'start' ? 'iniciar' : action === 'stop' ? 'parar' : 'reiniciar'} VM`,
      })
    } finally {
      setPowerActionLoading(false)
    }
  }

  const handleSendClipboard = () => {
    if (vncConsoleRef.current?.sendText && clipboardText) {
      vncConsoleRef.current.sendText(clipboardText)
      setShowClipboard(false)
      setClipboardText('')
    }
  }

  const vmData = consoleInfo
    ? {
        id: vmId,
        name: consoleInfo.name,
        status: consoleInfo.vm_status,
        ip:
          consoleInfo.ip_addresses && consoleInfo.ip_addresses.length > 0
            ? consoleInfo.ip_addresses[0]
            : consoleInfo.vm_status === 'running'
              ? 'Aguardando...'
              : 'N/A',
      }
    : null

  const renderConsoleCanvas = () => {
    if (isLoadingInfo) {
      return (
        <div className="flex size-full items-center justify-center">
          <div className="text-center">
            <LoaderIcon className="mx-auto mb-4 size-12 animate-spin text-white" />
            <p className="text-lg text-white">Carregando informações da VM...</p>
          </div>
        </div>
      )
    }

    if (!consoleInfo) {
      return (
        <div className="flex size-full items-center justify-center">
          <div className="text-center">
            <AlertCircleIcon className="mx-auto mb-4 size-12 text-red-500" />
            <p className="text-lg text-white">Erro ao carregar informações da VM</p>
          </div>
        </div>
      )
    }

    if (!consoleInfo.console_available) {
      return (
        <div className="flex size-full items-center justify-center">
          <div className="max-w-md text-center">
            <MonitorIcon className="mx-auto mb-4 size-16 text-gray-400" />
            <h3 className="mb-2 text-xl font-semibold text-white">Console Não Disponível</h3>
            <p className="mb-6 text-gray-300">
              A VM está {consoleInfo.vm_status === 'stopped' ? 'parada' : 'indisponível'}. O console
              só fica disponível quando a VM está em execução.
            </p>
            {consoleInfo.vm_status === 'stopped' && (
              <button
                onClick={() => handleVMAction('start')}
                disabled={powerActionLoading}
                className="rounded-lg bg-green-600 px-6 py-3 font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {powerActionLoading ? (
                  <>
                    <LoaderIcon className="mr-2 inline size-4 animate-spin" /> Iniciando...
                  </>
                ) : (
                  <>
                    <PlayIcon className="mr-2 inline size-4" /> Iniciar VM
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )
    }

    // Só o ticket do protocolo SELECIONADO monta o console: na troca (manual ou o fallback
    // automático para VNC, #1078) o ticket do protocolo anterior fica em mãos até a query
    // nova responder, e o VNCConsole montaria com a URL do serial.
    if (consoleTicket && consoleTicket.websocket_url && consoleTicket.protocol === consoleType) {
      if (consoleType === 'serial') {
        return (
          <SerialConsole
            ref={vncConsoleRef}
            websocketUrl={consoleTicket.websocket_url}
            instanceId={vmId}
            onConnect={() => {
              setIsConnected(true)
              setConnectionStatus('connected')
            }}
            onDisconnect={() => {
              setIsConnected(false)
              setConnectionStatus('disconnected')
              setVncMetrics(null)
              perguntarStatusAgora()
            }}
            onError={(error) => {
              setConsoleError(`Erro de conexão serial: ${error.message}`)
              setIsConnected(false)
              setConnectionStatus('error')
              setVncMetrics(null)
              perguntarStatusAgora()
            }}
            className="size-full"
          />
        )
      }

      return (
        <VNCConsole
          ref={vncConsoleRef}
          websocketUrl={consoleTicket.websocket_url}
          instanceId={vmId}
          vncTicket={consoleTicket.ticket || consoleTicket.password}
          onConnect={() => {
            setIsConnected(true)
            setConnectionStatus('connected')
          }}
          onDisconnect={() => {
            setIsConnected(false)
            setConnectionStatus('disconnected')
            setVncMetrics(null)
            // O WebSocket morre no instante em que a VM para — é o sinal mais rápido
            // que existe de que o status mudou, e o que o poll curto tentava alcançar.
            perguntarStatusAgora()
          }}
          onError={(error) => {
            setConsoleError(`Erro de conexão VNC: ${error.message}`)
            setIsConnected(false)
            setConnectionStatus('error')
            setVncMetrics(null)
            perguntarStatusAgora()
          }}
          onMetricsUpdate={(metrics) => {
            setVncMetrics(metrics)
          }}
          className="size-full"
        />
      )
    }

    return (
      <div className="flex size-full items-center justify-center">
        <div className="text-center">
          <LoaderIcon className="mx-auto mb-4 size-12 animate-spin text-white" />
          <p className="text-lg text-white">Obtendo acesso ao console...</p>
          <p className="mt-2 text-sm text-gray-400">
            Gerando ticket {consoleType === 'serial' ? 'Serial' : 'VNC'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-black">
      {/* Console Toolbar */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 bg-gray-800 px-3 py-2 sm:px-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white sm:text-sm">
          <div className="flex items-center space-x-2">
            <TerminalIcon className="size-4" />
            <span>
              Console {consoleType === 'serial' ? 'Serial' : 'VNC'} -{' '}
              {vmData?.name || 'Carregando...'}
            </span>
          </div>
          {vmData?.ip && vmData.ip !== 'N/A' && (
            <div className="flex items-center space-x-2">
              <WifiIcon className="size-4" />
              <span>{vmData.ip}</span>
            </div>
          )}
          {vncMetrics?.resolution && vncMetrics.resolution !== 'N/A' && (
            <div className="flex items-center space-x-2">
              <MonitorIcon className="size-4" />
              <span>{vncMetrics.resolution}</span>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2 text-xs text-white sm:text-sm">
          <span
            className={`rounded px-2 py-0.5 text-xs ${
              connectionStatus === 'connected'
                ? 'bg-green-600'
                : connectionStatus === 'error'
                  ? 'bg-red-600'
                  : 'bg-yellow-600'
            }`}
          >
            {connectionStatus === 'connected'
              ? 'Conectado'
              : connectionStatus === 'error'
                ? 'Erro'
                : connectionStatus === 'disconnected'
                  ? 'Desconectado'
                  : 'Conectando...'}
          </span>
        </div>
      </div>

      {/* Control Bar */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-gray-600 bg-gray-700 px-3 py-2 sm:px-4">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => handleVMAction('start')}
            disabled={powerActionLoading || vmData?.status === 'running'}
            className="rounded bg-green-600 p-2 text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
            title="Iniciar VM"
          >
            <PlayIcon className="size-4" />
          </button>
          <button
            onClick={() => handleVMAction('stop')}
            disabled={powerActionLoading || vmData?.status === 'stopped'}
            className="rounded bg-red-600 p-2 text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            title="Parar VM"
          >
            <SquareIcon className="size-4" />
          </button>
          <button
            onClick={() => handleVMAction('restart')}
            disabled={powerActionLoading || vmData?.status !== 'running'}
            className="rounded bg-yellow-600 p-2 text-white hover:bg-yellow-700 disabled:cursor-not-allowed disabled:opacity-50"
            title="Reiniciar VM"
          >
            <RotateCcwIcon className="size-4" />
          </button>
          <button
            onClick={() => handleVMAction('reset')}
            disabled={powerActionLoading || vmData?.status !== 'running'}
            className="rounded bg-orange-600 p-2 text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
            title="Reset"
          >
            <PowerIcon className="size-4" />
          </button>
        </div>

        <div className="flex items-center space-x-2">
          {/* Protocol toggle */}
          {consoleInfo?.serial_available && (
            <div className="flex items-center overflow-hidden rounded border border-gray-500">
              <button
                onClick={() => setConsoleType('serial')}
                className={`px-2 py-1 text-xs font-medium transition-colors ${
                  consoleType === 'serial'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                }`}
              >
                Serial
              </button>
              <button
                onClick={() => setConsoleType('vnc')}
                className={`px-2 py-1 text-xs font-medium transition-colors ${
                  consoleType === 'vnc'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                }`}
              >
                VNC
              </button>
            </div>
          )}
          {consoleType === 'vnc' && (
            <>
              <button
                onClick={() => handleKeyboardShortcut('Ctrl+Alt+Del')}
                disabled={!isConnected}
                className="rounded bg-gray-600 px-3 py-1 text-xs text-white hover:bg-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
                title="Ctrl+Alt+Del"
              >
                Ctrl+Alt+Del
              </button>
              <button
                onClick={() => setShowClipboard(true)}
                disabled={!isConnected}
                className="rounded bg-gray-600 p-2 text-white hover:bg-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
                title="Clipboard"
              >
                <ClipboardIcon className="size-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Console Canvas */}
      <div className="relative flex-1 overflow-hidden">
        <div className="absolute inset-0">{renderConsoleCanvas()}</div>

        {/* Clipboard Modal */}
        {showClipboard && (
          <div className="bg-opacity-50 absolute inset-0 z-20 flex items-center justify-center bg-black">
            <div className="mobile-modal-surface mx-4 w-full max-w-md rounded-lg bg-white p-6">
              <h3 className="mb-4 flex items-center text-lg font-semibold">
                <CopyIcon className="mr-2 size-5" />
                Clipboard Manual
              </h3>
              <textarea
                value={clipboardText}
                onChange={(e) => setClipboardText(e.target.value)}
                className="h-32 w-full rounded-md border border-gray-300 p-3 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                placeholder="Cole o texto aqui..."
              />
              <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-3">
                <button
                  onClick={() => {
                    setShowClipboard(false)
                    setClipboardText('')
                  }}
                  className="rounded bg-gray-100 px-4 py-2 text-gray-600 transition-colors hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSendClipboard}
                  className="rounded bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
                >
                  Enviar para VM
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Console Footer */}
      <div className="shrink-0 bg-gray-800 px-4 py-2 text-xs text-white">
        {isMobile ? (
          <div className="flex items-center justify-between">
            <span>{consoleType === 'serial' ? 'Serial/WebSocket' : 'VNC/WebSocket'}</span>
            {consoleType === 'vnc' && vncMetrics && isConnected ? (
              <span>{vncMetrics.fps} FPS</span>
            ) : null}
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {consoleType === 'serial' ? (
                <span className="flex items-center space-x-1">
                  <TerminalIcon className="size-3" />
                  <span>Copy/Paste Nativo</span>
                </span>
              ) : (
                <>
                  <span className="flex items-center space-x-1">
                    <KeyboardIcon className="size-3" />
                    <span>Teclado e Mouse</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <CopyIcon className="size-3" />
                    <span>Clipboard Bidirecional</span>
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center space-x-3">
              {consoleType === 'vnc' && vncMetrics && isConnected && (
                <>
                  <span>FPS: {vncMetrics.fps}</span>
                  <span>Banda: {vncMetrics.bandwidth.toFixed(1)} KB/s</span>
                  <span>|</span>
                </>
              )}
              <span>
                Protocolo: {consoleType === 'serial' ? 'Serial/WebSocket' : 'VNC/WebSocket'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmModal?.show && (
        <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4">
          <div className="mobile-modal-surface mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="mb-4 flex items-center text-lg font-semibold text-gray-900">
              <AlertCircleIcon className="mr-2 size-5 text-orange-500" />
              Confirmar Ação
            </h3>
            <p className="mb-6 text-gray-700">{confirmModal.message}</p>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-3">
              <button
                onClick={() => setConfirmModal(null)}
                className="rounded bg-gray-100 px-4 py-2 text-gray-600 transition-colors hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                onClick={() => executeVMAction(confirmModal.action)}
                className="rounded bg-orange-600 px-4 py-2 text-white transition-colors hover:bg-orange-700"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {alertModal?.show && (
        <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4">
          <div className="mobile-modal-surface mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3
              className={`mb-4 flex items-center text-lg font-semibold ${
                alertModal.type === 'success' ? 'text-green-700' : 'text-red-700'
              }`}
            >
              {alertModal.type === 'success' ? (
                <CheckCircleIcon className="mr-2 size-5" />
              ) : (
                <AlertCircleIcon className="mr-2 size-5" />
              )}
              {alertModal.type === 'success' ? 'Sucesso' : 'Erro'}
            </h3>
            <p className="mb-6 text-gray-700">{alertModal.message}</p>
            <div className="flex justify-end">
              <button
                onClick={() => setAlertModal(null)}
                className={`rounded px-4 py-2 text-white transition-colors ${
                  alertModal.type === 'success'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VMConsolePopupPage
